import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import axios from 'axios';
import Cookies from 'js-cookie';
import AppHeader from "../appHeader/appHeader";
import AppMenu from "../appMenu/appMenu";
import AppAdmin from "../appMenu/appAdmin";
import AppSetting from "../appSetting/appSetting";
import AppFooter from "../appFooter/appFooter";

/**
 * Este componente permite que administradores editem as informações de usuários do portal.
 * Realiza a autenticação do administrador, busca os dados do usuário a ser editado,
 * e atualiza as informações através de um formulário controlado.
 */
function EditarUsuarios() {
  const { id } = useParams(); // Obtém o ID do usuário a ser editado da URL
  const navigate = useNavigate(); // Hook para redirecionamento

  // Estados para gerenciar autenticação, dados do usuário, clientes e erros
  const [authenticated, setAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // Dados do administrador autenticado
  const [userData, setUserData] = useState({
    nome: '',
    email: '',
    nome_fantasia: '',
    telefone1: '',
    telefone2: '',
    endereço: '',
    numero: '',
    complemento: '',
    uf: '',
    cidade: '',
    cep: '',
    cpf_cnpj: '',
    senha: '',
    confirmarSenha: ''
  });
  const [clientData, setClientData] = useState({ nome: '' });
  const [errors, setErrors] = useState([]); // Lista de erros para exibição

  /**
   * useEffect para autenticação e obtenção dos dados do usuário a ser editado.
   * Executa ao montar o componente.
   */
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.post(`/api/auth`, { id }, { withCredentials: true });
        if (response.data.status === 200) {
          setAuthenticated(true);
          setCurrentUser(response.data.user); // Dados do administrador
          setClientData(response.data.clienteInfo); // Dados do cliente associado
          setUserData(response.data.userInfo); // Dados do usuário a ser editado

          // Combina os dados do usuário com o nome do cliente
          setUserData({
            ...response.data.userInfo, // Mantém todos os dados originais do usuário
            cliente: response.data.clienteInfo.nome // Adiciona o novo atributo "cliente"
          });
        } else {
          navigate('/login'); // Redireciona para login se autenticação falhar
        }
      } catch (error) {
        console.error('Erro na autenticação:', error);
        navigate('/login'); // Redireciona para login em caso de erro
      }
    };

    fetchUserData();
    
  }, [id, navigate]);

  /**
   * Função para formatar o campo CPF/CNPJ.
   * @param {object} e - Evento de input
   */
  const formatCpf_Cnpj = (e) => {
    const { value } = e.target;
    const digits = value.replace(/\D/g, "");

    let formattedValue = "";
    if (digits.length <= 11) {
      // CPF: 000.000.000-00
      formattedValue = digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else {
      // CNPJ: 00.000.000/0000-00
      formattedValue = digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }

    setUserData(prevData => ({ ...prevData, cpf_cnpj: formattedValue }));
  };

  /**
   * Função para formatar os campos de telefone.
   * @param {object} e - Evento de input
   */
  const formatTelefone = (e) => {
    const { name, value } = e.target;
    const digits = value.replace(/\D/g, "");

    let formattedValue = "";
    if (digits.length <= 10) {
      // Telefone fixo: (00) 0000-0000
      formattedValue = digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    } else {
      // Celular: (00) 00000-0000
      formattedValue = digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }

    setUserData(prevData => ({ ...prevData, [name]: formattedValue }));
  };

  /**
   * Função para formatar o campo CEP.
   * @param {object} e - Evento de input
   */
  const formatCep = (e) => {
    const { value } = e.target;
    const digits = value.replace(/\D/g, "");

    let formattedValue = "";
    if (digits.length <= 8) {
      // CEP: 00000-000
      formattedValue = digits.replace(/(\d{5})(\d{3})/, "$1-$2");
    }

    setUserData(prevData => ({ ...prevData, cep: formattedValue }));
  };

  /**
   * Função para lidar com as mudanças nos campos do formulário.
   * @param {object} event - Evento de mudança
   */
  const handleChange = (event) => {
    const { name, value } = event.target;
    setUserData(prevData => ({ ...prevData, [name]: value }));
  };

  console.log(userData)

  /**
   * Função para lidar com a submissão do formulário de edição.
   * @param {object} event - Evento de submissão
   */
  const handleSubmit = async (event) => {
    event.preventDefault(); // Previne o comportamento padrão do formulário

    // Limpa erros anteriores
    setErrors([]);

    try {
      const response = await axios.post(`/api/update/user/${id}`, userData, { withCredentials: true });
      if (response.data.status === 200) {
        // Redireciona para a lista de usuários após atualização bem-sucedida
        navigate('/consultar/usuarios');
      } else if (response.data.status === 400) {
        // Define os erros recebidos para exibição
        setErrors(response.data.erros);
        console.log(response.data)
        // Realça os campos com erro
        response.data.erros.forEach(err => {
          const field = document.getElementById(err.elemento);
          if (field) {
            field.style.backgroundColor = '#FFC0CB';
          }
        });
      } else {
        console.log(response)
        alert(response.data.msg || 'Ocorreu um erro ao atualizar o usuário.');
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      alert('Ocorreu um erro ao atualizar o usuário. Por favor, tente novamente.');
    }
  };

  // Estilos para o spinner de carregamento
  const spinnerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh"
  };

  /**
   * Renderiza o spinner de carregamento enquanto o usuário não está autenticado.
   */
  if (!authenticated) {
    return (
      <div style={spinnerStyle}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>

      {/* Cabeçalho da Página */}
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Editando Usuário</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Início</a></li>
                <li className="breadcrumb-item active">Editar Usuário</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Conteúdo Principal */}
      <section className="content">
        <div className="container-fluid" style={{ marginTop: 18 }}>
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {/* Exibição de erros, se houver */}
                  <div id='erros'>
                    {errors.map((error, index) => (
                      <div key={index} className="row mt-3" style={{ padding: '10px', borderRadius: '5px', backgroundColor: '#FFC0CB' }}>
                        <div className="col-12 text-center" style={{ color: '#EF5350', fontWeight: 'bold' }}>
                          {error.mensagem}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Formulário de Edição */}
                  <form onSubmit={handleSubmit}>
                    <div className="white-box">
                      <div className="tab-content">
                        <div className="row">
                          <div className="col-6">
                            <div className="form-group">
                              <label className="required">Nome de usuário</label>
                              <input
                                type="text"
                                name='nome'
                                className="form-control"
                                id="nome"
                                value={userData.nome}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="form-group">
                              <label className="required">Nome Fantasia</label>
                              <input
                                type="text"
                                name='nome_fantasia'
                                className="form-control"
                                id="fantasia"
                                value={userData.nome_fantasia}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-6">
                            <div className="form-group">
                              <label className="required">Telefone 1</label>
                              <input
                                type="text"
                                className="form-control"
                                name="telefone1"
                                id="telefone1"
                                value={userData.telefone1}
                                onChange={handleChange}
                                onInput={formatTelefone}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="form-group">
                              <label>Telefone 2</label>
                              <input
                                type="text"
                                className="form-control"
                                name="telefone2"
                                id="telefone2"
                                value={userData.telefone2}
                                onChange={handleChange}
                                onInput={formatTelefone}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-6">
                            <div className="form-group">
                              <label className="required">Email</label>
                              <input
                                type="email"
                                className="form-control"
                                name="email"
                                id="email"
                                value={userData.email}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="form-group">
                              <label className="required">Qual empresa pertence?</label>
                              <div className="input-group">
                                <select
                                  className="form-control"
                                  id="cliente"
                                  name="cliente"
                                  value={clientData.nome}
                                  disabled
                                >
                                  <option value="">Selecione uma empresa</option>
                                  {clientData.nome && <option value={clientData.nome}>{clientData.nome}</option>}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-6">
                            <div className="form-group">
                              <label>Endereço</label>
                              <input
                                type="text"
                                className="form-control"
                                name="endereço"
                                id="endereco"
                                value={userData.endereço}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="col-2">
                            <div className="form-group">
                              <label>N°</label>
                              <input
                                type="text"
                                className="form-control"
                                name="numero"
                                id="numero"
                                value={userData.numero}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="form-group">
                              <label>Complemento</label>
                              <input
                                type="text"
                                className="form-control"
                                name='complemento'
                                id="complemento"
                                value={userData.complemento}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-2">
                            <div className="form-group">
                              <label>U.F.</label>
                              <select
                                className="form-control"
                                id="uf"
                                name="uf"
                                value={userData.uf}
                                onChange={handleChange}
                              >
                                <option value="">Selecione</option>
                                {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map(estado => (
                                  <option key={estado} value={estado}>{estado}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="col-2">
                            <div className="form-group">
                              <label>Cidade</label>
                              <input
                                type="text"
                                className="form-control"
                                name='cidade'
                                id="cidade"
                                value={userData.cidade}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="form-group">
                              <label>CEP</label>
                              <input
                                type="text"
                                className="form-control"
                                name='cep'
                                id="cep"
                                value={userData.cep}
                                onChange={handleChange}
                                onInput={formatCep}
                              />
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="form-group">
                              <label className="required">CPF/CNPJ</label>
                              <input
                                type="text"
                                className="form-control"
                                id="cpf_cnpj"
                                name="cpf_cnpj"
                                value={userData.cpf_cnpj}
                                onChange={handleChange}
                                onInput={formatCpf_Cnpj}
                                required
                              />
                            </div>
                          </div>
                        </div>

                        <div className='row'>
                          <div className="col-6">
                            <div className="form-group">
                              <label>Nova Senha</label>
                              <input
                                type="password"
                                className="form-control"
                                id="senha"
                                name="senha"
                                value={userData.senha}
                                onChange={handleChange}
                              />
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="form-group">
                              <label>Confirmar Nova Senha</label>
                              <input
                                type="password"
                                className="form-control"
                                id="confirmarSenha"
                                name="confirmarSenha"
                                value={userData.confirmarSenha}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Botão de Submissão */}
                        <div className="row">
                          <div className="col-12 text-center">
                            <button type="submit" className="btn btn-success" id="salvar">
                              Salvar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}

export default EditarUsuarios;
